
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/datepicker";
import { Baby, Droplet, Image, Milk, Weight, X } from "lucide-react";
import { toast } from "sonner";
import { generateDiaryEntry } from "@/utils/generator";
import { calculateAge } from "@/utils/ageCalculator";
import { useNavigate } from "react-router-dom";
import { getBabyProfileSync, hasBabyProfile } from "@/utils/babyProfile";
import { BabyProfileDialog } from "./BabyProfileDialog";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

export interface DiaryGeneratorProps {
  onGenerate: (params: any) => void;
}

const DiaryGenerator = ({ onGenerate }: DiaryGeneratorProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [babyName, setBabyName] = useState("");
  const [age, setAge] = useState("");
  const [babyBirthday, setBabyBirthday] = useState<Date | undefined>(undefined);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedMilestones, setSelectedMilestones] = useState<string[]>([]);
  const [mood, setMood] = useState("happy");
  const [notes, setNotes] = useState("");
  const [generatedEntry, setGeneratedEntry] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [diaperChanges, setDiaperChanges] = useState("");
  const [milkIntake, setMilkIntake] = useState("");
  const [hasProfile, setHasProfile] = useState(false);

  // Get translated activity options
  const activities = [
    { key: "tummyTime", value: t('generate.activities.tummyTime', language) },
    { key: "bathTime", value: t('generate.activities.bathTime', language) },
    { key: "reading", value: t('generate.activities.reading', language) },
    { key: "play", value: t('generate.activities.play', language) },
    { key: "feeding", value: t('generate.activities.feeding', language) },
    { key: "nap", value: t('generate.activities.nap', language) },
    { key: "outdoors", value: t('generate.activities.outdoors', language) },
    { key: "music", value: t('generate.activities.music', language) }
  ];

  // Get translated milestone options
  const milestones = [
    { key: "firstSmile", value: t('generate.milestones.firstSmile', language) },
    { key: "rolledOver", value: t('generate.milestones.rolledOver', language) },
    { key: "firstWord", value: t('generate.milestones.firstWord', language) },
    { key: "firstSteps", value: t('generate.milestones.firstSteps', language) },
    { key: "firstTooth", value: t('generate.milestones.firstTooth', language) },
    { key: "firstLaugh", value: t('generate.milestones.firstLaugh', language) },
    { key: "sittingUp", value: t('generate.milestones.sittingUp', language) }
  ];

  // Get mood options with translations
  const moods = [
    { value: "happy", label: t('generate.moods.happy', language) },
    { value: "calm", label: t('generate.moods.calm', language) },
    { value: "curious", label: t('generate.moods.curious', language) },
    { value: "fussy", label: t('generate.moods.fussy', language) },
    { value: "sleepy", label: t('generate.moods.sleepy', language) }
  ];

  useEffect(() => {
    const profile = getBabyProfileSync();
    setHasProfile(hasBabyProfile());
    
    if (profile) {
      setBabyName(profile.name || "");
      
      if (profile.birthday) {
        const birthDate = new Date(profile.birthday);
        setBabyBirthday(birthDate);
        setAge(calculateAge(birthDate, language));
      }
    }
  }, [language]);
  
  useEffect(() => {
    if (babyBirthday) {
      setAge(calculateAge(babyBirthday, language));
    }
  }, [babyBirthday, language]);

  const toggleActivity = (activity: string) => {
    setSelectedActivities(prev =>
      prev.includes(activity)
        ? prev.filter(a => a !== activity)
        : [...prev, activity]
    );
  };

  const toggleMilestone = (milestone: string) => {
    setSelectedMilestones(prev =>
      prev.includes(milestone)
        ? prev.filter(m => m !== milestone)
        : [...prev, milestone]
    );
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      Array.from(e.target.files).forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (event) => {
            if (event.target && typeof event.target.result === 'string') {
              setImages(prev => [...prev, event.target.result as string]);
            }
          };
          reader.readAsDataURL(file);
        }
      });
    }
  };

  const removeImage = (indexToRemove: number) => {
    setImages(prevImages => prevImages.filter((_, index) => index !== indexToRemove));
  };

  const handleGenerate = () => {
    if (!babyName) {
      toast.error(language === 'ja' ? "赤ちゃんの名前を入力してください" : "Please enter your baby's name");
      return;
    }

    if (!age) {
      toast.error(language === 'ja' ? "赤ちゃんの年齢を入力してください" : "Please enter your baby's age");
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      try {
        const entry = generateDiaryEntry({
          babyName,
          age,
          date: new Date(),
          activities: selectedActivities,
          milestones: selectedMilestones,
          mood,
          additionalNotes: notes,
          height,
          weight,
          diaperChanges,
          milkIntake
        });
        
        setGeneratedEntry(entry);
        setLoading(false);
      } catch (error) {
        console.error("Error generating entry:", error);
        toast.error(t('generate.errorMessage', language));
        setLoading(false);
      }
    }, 1000); // Simulate processing time
  };

  const handleSave = () => {
    if (!generatedEntry) return;
    
    const entryData = {
      id: Date.now().toString(),
      babyName,
      age,
      activities: selectedActivities,
      milestones: selectedMilestones,
      mood,
      additionalNotes: notes,
      entryText: generatedEntry,
      createdAt: new Date().toISOString(),
      images: images,
      height,
      weight,
      diaperChanges,
      milkIntake
    };
    
    onGenerate(entryData);
  };

  return (
    <div className="container max-w-5xl mx-auto px-4 py-12 animate-fade-up">
      <Card className="overflow-hidden glass-card border shadow-lg">
        <CardHeader className="bg-accent/50 pb-8">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl">
                {t('generate.title', language)}
              </CardTitle>
              <CardDescription>
                {t('generate.subtitle', language)}
              </CardDescription>
            </div>
            <BabyProfileDialog />
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="babyName">{t('generate.form.babyName', language)}</Label>
                <Input 
                  id="babyName" 
                  value={babyName} 
                  onChange={(e) => setBabyName(e.target.value)} 
                  placeholder={t('generate.form.babyName', language)}
                  readOnly={hasProfile}
                  className={hasProfile ? "bg-slate-50" : ""}
                />
                {hasProfile && (
                  <p className="text-xs text-muted-foreground">{t('generate.form.babyNameLoaded', language)}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{t('generate.form.babyBirthday', language)}</Label>
                <DatePicker 
                  date={babyBirthday} 
                  onDateChange={setBabyBirthday}
                  className={hasProfile ? "opacity-75 pointer-events-none" : ""}
                />
                {babyBirthday && (
                  <p className="text-sm text-muted-foreground">
                    {t('generate.form.currentAge', language)}: {age}
                  </p>
                )}
                {hasProfile && babyBirthday && (
                  <p className="text-xs text-muted-foreground">{t('generate.form.babyNameLoaded', language)}</p>
                )}
              </div>
              
              {!babyBirthday && (
                <div className="space-y-2">
                  <Label htmlFor="age">{t('generate.form.babyAge', language)}</Label>
                  <Input 
                    id="age" 
                    value={age} 
                    onChange={(e) => setAge(e.target.value)} 
                    placeholder={t('generate.form.activityPlaceholder', language)}
                  />
                </div>
              )}
              
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-medium flex items-center gap-2">
                  <Baby className="h-4 w-4" />
                  {t('generate.form.babyMetrics', language)}
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="height" className="flex items-center gap-1 text-xs">
                      {t('generate.form.height', language)}
                    </Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        id="height" 
                        type="text"
                        value={height} 
                        onChange={(e) => setHeight(e.target.value)} 
                        placeholder={t('generate.form.height', language)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="weight" className="flex items-center gap-1 text-xs">
                      <Weight className="h-3 w-3" />
                      {t('generate.form.weight', language)}
                    </Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        id="weight" 
                        type="text"
                        value={weight} 
                        onChange={(e) => setWeight(e.target.value)} 
                        placeholder={t('generate.form.weight', language)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="diaperChanges" className="flex items-center gap-1 text-xs">
                      <Droplet className="h-3 w-3" />
                      {t('generate.form.diaperChanges', language)}
                    </Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        id="diaperChanges" 
                        type="text"
                        value={diaperChanges} 
                        onChange={(e) => setDiaperChanges(e.target.value)} 
                        placeholder={t('generate.form.diaperChanges', language)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="milkIntake" className="flex items-center gap-1 text-xs">
                      <Milk className="h-3 w-3" />
                      {t('generate.form.milkIntake', language)}
                    </Label>
                    <div className="flex gap-2 items-center">
                      <Input 
                        id="milkIntake" 
                        type="text"
                        value={milkIntake} 
                        onChange={(e) => setMilkIntake(e.target.value)} 
                        placeholder={t('generate.form.milkIntake', language)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mood">{t('generate.form.moodToday', language)}</Label>
                <Select value={mood} onValueChange={setMood}>
                  <SelectTrigger id="mood">
                    <SelectValue placeholder={t('generate.form.mood', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    {moods.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="images">{t('generate.form.addPhotos', language)}</Label>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
                    >
                      <Image size={18} />
                      <span>{t('generate.form.uploadImages', language)}</span>
                    </label>
                    <Input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </div>
                  
                  {images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {images.map((src, index) => (
                        <div key={index} className="relative rounded-md overflow-hidden aspect-square">
                          <img 
                            src={src} 
                            alt={`Uploaded preview ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            className="absolute top-1 right-1 h-6 w-6"
                            onClick={() => removeImage(index)}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                <Label>{t('generate.form.activitiesToday', language)}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {activities.map((activity) => (
                    <div key={activity.key} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`activity-${activity.key}`} 
                        checked={selectedActivities.includes(activity.key)}
                        onCheckedChange={() => toggleActivity(activity.key)}
                      />
                      <Label 
                        htmlFor={`activity-${activity.key}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {activity.value}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>{t('generate.form.milestones', language)}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {milestones.map((milestone) => (
                    <div key={milestone.key} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`milestone-${milestone.key}`} 
                        checked={selectedMilestones.includes(milestone.key)}
                        onCheckedChange={() => toggleMilestone(milestone.key)}
                      />
                      <Label 
                        htmlFor={`milestone-${milestone.key}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {milestone.value}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">{t('generate.form.additionalNotes', language)}</Label>
                <Textarea 
                  id="notes" 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)} 
                  placeholder={t('generate.placeholder.additionalNotes', language)}
                  className="min-h-[120px]"
                />
              </div>
            </div>
          </div>
          
          <Button 
            className="w-full mt-8" 
            size="lg"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? t('generate.form.generating', language) : t('generate.form.generateEntry', language)}
          </Button>
        </CardContent>
        
        {generatedEntry && (
          <div className="px-6 pb-6">
            <Card className="border bg-background/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{t('generate.form.generatedEntryTitle', language)}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="whitespace-pre-line text-sm md:text-base leading-relaxed">
                  {generatedEntry}
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleSave} 
                  className="w-full"
                >
                  {t('generate.form.saveEntry', language)}
                </Button>
              </CardFooter>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DiaryGenerator;
